import type { LucideIcon } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';

import { Button } from './button.tsx';
import { icons } from './icons.ts';
import { ListTray, QuickFooter } from './item-picker-footer.tsx';

// The item picker (AVenue Design System ItemPicker, approved 2026-10-17): one searchable list for
// adding lines, in place of a long native select. Three things are load-bearing:
//
// 1. It stays open after every add. Enter or a click adds and clears the search, so the next item is
//    typed straight away; Shift+Enter adds and keeps the search, for several of a family. Esc clears
//    the search, then closes; Done closes. A built list is never dropped without asking.
// 2. It shows only what the data behind it can back (CB-CONV §7, CB-REQ ADO-06). Availability for the
//    quote's window, kits and cross-hire are in the design but have no API yet, so a row says only
//    what its caller passes in `note`.
// 3. It is one ARIA combobox and listbox (INV-13). Focus stays in the search; the highlighted row is
//    `aria-activedescendant`. A row holds no buttons (an option may not): a click on it adds or
//    picks, and a list's steppers are in the tray below.
//
// Adding is the caller's: `onAdd` resolves when the lines are on, and rejects with the server's
// sentence, which the picker shows without losing the search or the list being built. It resolves
// `'handled'` when it took the add elsewhere (a form for an item's hours): the picker then says
// nothing and keeps everything as it was.

export type PickerGroup = 'hire' | 'venue' | 'service';

export interface PickerItem {
  readonly id: string;
  readonly name: string;
  readonly group: PickerGroup;
  readonly icon: LucideIcon;
  /** Under the name, and searched: category, manufacturer and model. */
  readonly meta?: string | undefined;
  /** The right-hand fact the caller can back, e.g. "16 owned". */
  readonly note?: string | undefined;
  /** How many are on this quote already. */
  readonly onQuote?: number | undefined;
}

export interface PickedEntry {
  readonly id: string;
  readonly quantity: number;
}

/** Thrown by `onAdd` when some entries went on before one was refused. */
export class PartlyAdded extends Error {
  constructor(
    message: string,
    readonly added: readonly string[],
  ) {
    super(message);
    this.name = 'PartlyAdded';
  }
}

type Filter = 'all' | 'hire' | 'service';

const GROUPS: ReadonlyArray<readonly [PickerGroup, string]> = [
  ['hire', 'Hire stock'],
  ['venue', 'Venue equip'],
  ['service', 'Services'],
];
const FILTERS: ReadonlyArray<readonly [Filter, string]> = [
  ['all', 'All'],
  ['hire', 'Hire stock'],
  ['service', 'Services'],
];
const inFilter = (item: PickerItem, filter: Filter) =>
  filter === 'all' || (filter === 'hire' ? item.group !== 'service' : item.group === 'service');

function highlight(name: string, tokens: readonly string[]): ReactNode[] {
  if (tokens.length === 0) return [name];
  const lower = name.toLowerCase();
  const marked = new Array<boolean>(name.length).fill(false);
  for (const token of tokens) {
    for (let at = lower.indexOf(token); at > -1; at = lower.indexOf(token, at + token.length)) {
      marked.fill(true, at, at + token.length);
    }
  }
  const parts: ReactNode[] = [];
  let start = 0;
  for (let i = 1; i <= name.length; i++) {
    if (i === name.length || marked[i] !== marked[start]) {
      const text = name.slice(start, i);
      parts.push(
        marked[start] ? (
          <mark key={start} className="rounded-[3px] bg-accent-600/16 text-accent-800">
            {text}
          </mark>
        ) : (
          text
        ),
      );
      start = i;
    }
  }
  return parts;
}

/** The search's matches, best first: names starting with it, then containing it, then the rest. */
function search(items: readonly PickerItem[], query: string) {
  const q = query.trim().toLowerCase();
  const tokens = q ? q.split(/\s+/) : [];
  const found = items
    .map((item, order) => {
      const hay = `${item.name} ${item.meta ?? ''}`.toLowerCase();
      if (!tokens.every((t) => hay.includes(t))) return null;
      const name = item.name.toLowerCase();
      const rank = !q ? 0 : name.startsWith(q) ? 0 : name.includes(tokens[0]!) ? 1 : 2;
      return { item, rank, order };
    })
    .filter((m) => m !== null)
    .sort((a, b) => a.rank - b.rank || a.order - b.order)
    .map((m) => m.item);
  return { tokens, found };
}

export function ItemPicker({
  items,
  loading = false,
  label,
  scopeLabel,
  onAdd,
  onClose,
  above,
}: {
  items: readonly PickerItem[];
  /** The catalogue is still loading: say so, rather than that it is empty. */
  loading?: boolean;
  /** The field's accessible name, e.g. "Add a line". */
  label: string;
  /** Where lines go, said in the confirmation: "to Plenary 1". */
  scopeLabel?: string | undefined;
  onAdd: (entries: readonly PickedEntry[], mode: 'quick' | 'multi') => Promise<void | 'handled'>;
  onClose: () => void;
  /** Controls shown above the search, e.g. the room lines go into. */
  above?: ReactNode;
}) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [mode, setMode] = useState<'quick' | 'multi'>('quick');
  const [active, setActive] = useState(0);
  const [picks, setPicks] = useState<Readonly<Record<string, number>>>({});
  const [adding, setAdding] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [added, setAdded] = useState<string | null>(null);
  const [leaving, setLeaving] = useState(false);

  const { tokens, found } = search(items, query);
  const counts = Object.fromEntries(
    FILTERS.map(([f]) => [f, found.filter((i) => inFilter(i, f)).length]),
  );
  const groups = GROUPS.map(([group, title]) => ({
    title,
    items: found.filter((i) => inFilter(i, filter) && i.group === group),
  })).filter((g) => g.items.length > 0);
  const flat = groups.flatMap((g) => g.items);
  const current = flat.length > 0 ? flat[Math.min(active, flat.length - 1)] : undefined;
  const byId = new Map(items.map((i) => [i.id, i]));
  const picked = Object.entries(picks);
  const to = scopeLabel ? ` to ${scopeLabel}` : '';
  const optionId = (itemId: string) => `${id}-option-${itemId}`;

  // Keep the highlighted row in view as the arrow keys move it.
  useEffect(() => {
    if (current)
      document.getElementById(optionId(current.id))?.scrollIntoView?.({ block: 'nearest' });
  });

  function typed(value: string) {
    setQuery(value);
    setActive(0);
    setProblem(null);
    setAdded(null);
  }

  async function add(entries: readonly PickedEntry[], keepQuery: boolean) {
    if (entries.length === 0 || adding) return;
    setAdding(true);
    setProblem(null);
    try {
      if ((await onAdd(entries, mode)) === 'handled') return;
      const first = byId.get(entries[0]!.id)?.name ?? 'the item';
      setAdded(entries.length === 1 ? `Added ${first}${to}` : `Added ${entries.length} lines${to}`);
      setPicks({});
      if (!keepQuery) {
        setQuery('');
        setActive(0);
      }
    } catch (error) {
      // Lines are added one at a time, so a list can be refused part-way. What did go on leaves the
      // list, or adding it again would put it on twice.
      if (error instanceof PartlyAdded) {
        setPicks((p) => {
          const next = { ...p };
          for (const done of error.added) delete next[done];
          return next;
        });
      }
      setProblem(error instanceof Error ? error.message : 'That could not be added.');
    } finally {
      setAdding(false);
      input.current?.focus();
    }
  }

  const bump = (itemId: string, by: number) =>
    setPicks((p) => {
      const next = { ...p, [itemId]: Math.max(0, (p[itemId] ?? 0) + by) };
      if (next[itemId] === 0) delete next[itemId];
      return next;
    });
  const choose = (itemId: string, keepQuery: boolean) =>
    mode === 'multi' ? bump(itemId, 1) : void add([{ id: itemId, quantity: 1 }], keepQuery);
  const commit = () =>
    void add(
      picked.map(([itemId, quantity]) => ({ id: itemId, quantity })),
      false,
    );
  // Closing drops a built list, so it asks first.
  const done = () => (picked.length > 0 ? setLeaving(true) : onClose());

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive(Math.max(0, Math.min(flat.length - 1, active + 1)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive(Math.max(0, active - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if ((event.metaKey || event.ctrlKey) && mode === 'multi') commit();
      else if (current) choose(current.id, event.shiftKey);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (query) typed('');
      else done();
    }
  }

  return (
    <section
      aria-label={label}
      className="flex flex-col overflow-hidden rounded-lg bg-surface shadow-[0_16px_40px_color-mix(in_srgb,#17202e_18%,transparent),inset_0_0_0_1px_var(--color-neutral-300)]"
    >
      <div className="flex flex-col gap-3 p-4 pb-3">
        {above}
        <div className="flex items-center gap-2.5 rounded-md border border-divider bg-surface px-3 text-muted focus-within:border-accent-600 focus-within:outline-2 focus-within:outline-accent-600/25">
          <icons.search size={16} aria-hidden />
          <label htmlFor={`${id}-search`} className="sr-only">
            {label}
          </label>
          <input
            ref={input}
            id={`${id}-search`}
            type="text"
            role="combobox"
            aria-expanded="true"
            aria-controls={`${id}-list`}
            aria-autocomplete="list"
            aria-activedescendant={current ? optionId(current.id) : undefined}
            autoComplete="off"
            // The picker is opened by pressing Add a line, so the search taking focus is expected.
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder="Search items and services"
            className="h-10 min-w-0 flex-1 border-0 bg-transparent text-sm text-ink outline-none placeholder:text-neutral-600"
            value={query}
            onChange={(e) => typed(e.currentTarget.value)}
            onKeyDown={onKeyDown}
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div
            role="radiogroup"
            aria-label="How to add"
            className="inline-flex overflow-hidden rounded-md border border-divider text-sm"
          >
            {(
              [
                ['quick', 'One at a time'],
                ['multi', 'Build a list'],
              ] as const
            ).map(([value, text]) => (
              <label
                key={value}
                className={`cursor-pointer px-3 py-1.5 has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-accent-600 [&+&]:border-l [&+&]:border-divider ${mode === value ? 'bg-accent-600 text-white' : 'hover:bg-ink/7'}`}
              >
                <input
                  type="radio"
                  name={`${id}-mode`}
                  className="sr-only"
                  checked={mode === value}
                  onChange={() => setMode(value)}
                />
                {text}
              </label>
            ))}
          </div>
          <span className="flex-1" />
          {FILTERS.map(([value, text]) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => {
                setFilter(value);
                setActive(0);
              }}
              className={`inline-flex h-[30px] items-center gap-1.5 rounded-[7.5px] border px-2.5 text-sm ${filter === value ? 'border-ink bg-ink text-white' : 'border-divider hover:bg-ink/7'}`}
            >
              {text}
              <span className={filter === value ? 'text-neutral-400' : 'text-neutral-600'}>
                {counts[value]}
              </span>
            </button>
          ))}
        </div>
        {problem ? (
          <p role="alert" className="alert">
            {problem}
          </p>
        ) : null}
        {leaving ? (
          <div
            role="alertdialog"
            aria-label="Drop the list?"
            className="notice flex items-center gap-3"
          >
            <span className="flex-1">{`Drop ${picked.length} picked?`}</span>
            <Button type="button" onClick={onClose}>
              Drop
            </Button>
            <Button type="button" variant="quiet" onClick={() => setLeaving(false)}>
              Keep
            </Button>
          </div>
        ) : null}
      </div>

      <div
        id={`${id}-list`}
        role="listbox"
        aria-label="Matching items"
        aria-multiselectable={mode === 'multi' || undefined}
        className="max-h-[420px] overflow-y-auto border-t border-rule px-2 pb-2"
      >
        {groups.map((group) => (
          <div key={group.title} role="group" aria-label={group.title}>
            <div
              aria-hidden="true"
              className="flex items-baseline gap-2 px-3 pt-3 pb-1 font-heading text-[10px] font-extrabold tracking-[0.1em] text-muted uppercase"
            >
              <span>{group.title}</span>
              <span className="font-semibold text-neutral-600">{group.items.length}</span>
            </div>
            {group.items.map((item) => (
              <Row
                key={item.id}
                id={optionId(item.id)}
                item={item}
                tokens={tokens}
                isActive={item === current}
                picked={mode === 'multi' ? (picks[item.id] ?? 0) : null}
                onPoint={() => setActive(flat.indexOf(item))}
                onChoose={() => {
                  choose(item.id, false);
                  input.current?.focus();
                }}
              />
            ))}
          </div>
        ))}
        {flat.length === 0 ? (
          <p className="px-3 py-7 text-sm text-muted">
            {loading
              ? 'Loading…'
              : query.trim()
                ? `No match for “${query.trim()}”. Add it in Inventory first.`
                : 'The catalogue is empty.'}
          </p>
        ) : null}
      </div>

      {mode === 'multi' ? (
        <ListTray
          picked={picked}
          nameOf={(itemId) => byId.get(itemId)?.name ?? 'Item'}
          adding={adding}
          added={added}
          to={to}
          onBump={bump}
          onDrop={(itemId) => bump(itemId, -(picks[itemId] ?? 0))}
          onClear={() => setPicks({})}
          onAdd={commit}
          onDone={done}
        />
      ) : (
        <QuickFooter adding={adding} added={added} onDone={done} />
      )}
    </section>
  );
}

/** One option. It holds no controls of its own: a click on it is the add, or the pick. */
function Row({
  id,
  item,
  tokens,
  isActive,
  picked,
  onPoint,
  onChoose,
}: {
  id: string;
  item: PickerItem;
  tokens: readonly string[];
  isActive: boolean;
  /** In Build a list, how many are picked; null otherwise. */
  picked: number | null;
  onPoint: () => void;
  onChoose: () => void;
}) {
  const Icon = item.icon;
  return (
    // Keyboard use is the search's (aria-activedescendant), so the option itself takes no focus.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      id={id}
      role="option"
      tabIndex={-1}
      aria-selected={picked === null ? isActive : picked > 0}
      onMouseEnter={onPoint}
      onClick={onChoose}
      className={`grid cursor-pointer grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 rounded-md py-1.5 pr-3 pl-1.5 ${isActive ? 'bg-accent-100 shadow-[inset_0_0_0_1px_var(--color-accent-300)]' : 'hover:bg-ink/4'}`}
    >
      <span className="flex min-w-0 items-center gap-2.5 px-1.5 py-1">
        <span
          className={`flex size-8 flex-none items-center justify-center rounded-sm shadow-[inset_0_0_0_1px_var(--color-rule)] ${isActive ? 'bg-surface text-accent-700' : 'bg-tile text-ink-soft'}`}
        >
          <Icon size={16} aria-hidden />
        </span>
        <span className="flex min-w-0 flex-col gap-0.5">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-ink">
              {highlight(item.name, tokens)}
            </span>
            {item.group === 'venue' ? (
              <span className="tag whitespace-nowrap shadow-[inset_0_0_0_1px_var(--color-accent-600)]">
                VENUE EQUIP
              </span>
            ) : null}
            {item.onQuote ? (
              <span className="tag tag-accent whitespace-nowrap">On quote × {item.onQuote}</span>
            ) : null}
          </span>
          {item.meta ? <span className="truncate text-xs text-muted">{item.meta}</span> : null}
        </span>
      </span>
      <span className="text-right text-xs whitespace-nowrap text-muted">{item.note}</span>
      <span aria-hidden="true" className="min-w-7 text-right font-heading text-sm font-bold">
        {picked ? (
          <span className="text-ink">× {picked}</span>
        ) : isActive ? (
          <span className="text-accent-600">{picked === null ? 'Add' : 'Pick'}</span>
        ) : null}
      </span>
    </div>
  );
}
